/**
 * LinearGauge Component
 *
 * An expandable gauge showing a CAMEL component score with comparison markers.
 * Can expand to show nested indicators.
 */

import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getScoreColor, calculatePercentage, clamp } from '../../utils';
import styles from './LinearGauge.module.css';

interface LinearGaugeProps {
  /** Display label for the gauge */
  label: string;
  /** Current value */
  currentValue: number;
  /** Quarter/previous average for comparison */
  quarterAvg: number;
  /** Minimum value (default 0) */
  min?: number;
  /** Maximum value */
  max: number;
  /** Unit to display (default empty) */
  unit?: string;
  /** Show delta comparison (default true) */
  showDelta?: boolean;
  /** Whether this gauge is expanded */
  isExpanded?: boolean;
  /** Toggle expansion callback */
  onToggle?: () => void;
  /** Child content (indicators) shown when expanded */
  children?: ReactNode;
}

export default function LinearGauge({
  label,
  currentValue,
  quarterAvg,
  min = 0,
  max = 100,
  unit = '',
  showDelta = true,
  isExpanded = false,
  onToggle,
  children,
}: LinearGaugeProps) {
  const { t } = useTranslation();
  const percentage = calculatePercentage(currentValue, min, max);
  const quarterPercentage = calculatePercentage(quarterAvg, min, max);
  const delta = currentValue - quarterAvg;

  const color = getScoreColor(currentValue, max);

  const getDeltaColor = (d: number): string => {
    if (d > 0) return '#00D4AA';
    if (d < 0) return '#FF4757';
    return 'var(--dashboard-text-tertiary)';
  };

  const scoreLabelPosition = clamp(percentage, 8, 92);
  const hasChildren = !!children;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (hasChildren && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onToggle?.();
    }
  };

  return (
    <div
      className={styles.gaugeContainer}
      role="region"
      aria-label={`${label} gauge: ${currentValue.toFixed(2)} out of ${max}`}
    >
      <div
        className={styles.gaugeHeader}
        onClick={hasChildren ? onToggle : undefined}
        onKeyDown={handleKeyDown}
        style={{ cursor: hasChildren ? 'pointer' : 'default' }}
        role={hasChildren ? 'button' : undefined}
        tabIndex={hasChildren ? 0 : undefined}
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-controls={hasChildren ? `${label.replace(/\s+/g, '-')}-indicators` : undefined}
      >
        <div className={styles.gaugeLabelRow}>
          <span className={styles.gaugeLabel}>{label}</span>
          {hasChildren && (
            <span className={styles.expandIcon} aria-hidden="true">
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </span>
          )}
        </div>

        <div className={styles.gaugeValues}>
          <span className={styles.currentValue} style={{ color }}>
            {currentValue.toFixed(2)}
            {unit}
          </span>
          {showDelta && (
            <span className={styles.deltaValue} style={{ color: getDeltaColor(delta) }}>
              {delta >= 0 ? '+' : ''}
              {delta.toFixed(2)}
              <span className={styles.deltaLabel}> {t('linearGauge.vsQuarter')}</span>
            </span>
          )}
        </div>
      </div>

      <div className={styles.gaugeTrackContainer}>
        <div
          className={styles.scoreLabelInline}
          style={{
            left: `${scoreLabelPosition}%`,
            color: color,
          }}
        >
          {currentValue.toFixed(1)}
        </div>

        <div className={styles.gaugeTrack}>
          <div className={styles.gaugeGradient} />

          <div
            className={styles.gaugeFill}
            style={{
              width: `${clamp(percentage, 0, 100)}%`,
              background: `linear-gradient(90deg, ${color}22 0%, ${color}55 100%)`,
            }}
          />

          <div
            className={styles.quarterMarkerTriangle}
            style={{ left: `${clamp(quarterPercentage, 0, 100)}%` }}
          >
            <svg width="10" height="6" viewBox="0 0 10 6" className={styles.triangleUp}>
              <polygon points="5,0 10,6 0,6" className={styles.quarterTriangleFill} />
            </svg>
            <span className={styles.quarterValue}>{quarterAvg.toFixed(1)}</span>
          </div>

          <div
            className={styles.currentMarkerTriangle}
            style={{ left: `${clamp(percentage, 0, 100)}%` }}
          >
            <svg width="12" height="8" viewBox="0 0 12 8" className={styles.triangleDown}>
              <polygon points="6,8 12,0 0,0" fill={color} />
            </svg>
          </div>
        </div>

        <div className={styles.gaugeRange}>
          <span>
            {min}
            {unit}
          </span>
          <span>
            {max}
            {unit}
          </span>
        </div>
      </div>

      {isExpanded && children && (
        <div
          id={`${label.replace(/\s+/g, '-')}-indicators`}
          className={styles.gaugeChildren}
          role="region"
          aria-label={`${label} indicators`}
        >
          {children}
        </div>
      )}
    </div>
  );
}
